import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  ArrowRight, 
  ArrowLeft,
  BookOpen,
  Target,
  Flag
} from "lucide-react";
import { useActivityContext } from "@/hooks/useActivityContext";

interface Question {
  id: string;
  text: string;
  options: Array<{
    id: string;
    text: string;
    is_correct: boolean;
  }>;
  explanation?: string;
}

interface TestInterfaceProps {
  questions: Question[];
  onSubmit: (results: { questionId: string; selectedOptionId: string }[]) => void;
  subjectName?: string;
  categoryName?: string;
  testMode?: "exam" | "practice";
  onTimeUpdate?: (time: number) => void;
  onBackToSelection?: () => void;
}

const TestInterface = ({ 
  questions, 
  onSubmit, 
  subjectName, 
  categoryName,
  testMode = "practice",
  onTimeUpdate
}: TestInterfaceProps) => {
  const { updateQuizDetails } = useActivityContext();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<{ [key: string]: string }>({});
  const [showExplanation, setShowExplanation] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);

  const currentQuestion = questions[currentQuestionIndex];
  const selectedAnswer = selectedAnswers[currentQuestion?.id];
  const correctOption = currentQuestion?.options.find(opt => opt.is_correct);
  const isCorrect = selectedAnswer === correctOption?.id;

  // Update activity context when question changes
  useEffect(() => {
    if (currentQuestion && questions.length > 0) {
      // Use setTimeout to avoid setState during render
      const timeoutId = setTimeout(() => {
        updateQuizDetails({
          currentQuestion: {
            id: currentQuestion.id,
            text: currentQuestion.text,
            options: currentQuestion.options,
            explanation: currentQuestion.explanation
          },
          totalQuestions: questions.length,
          currentQuestionIndex,
          selectedAnswers,
          timeElapsed,
          subject: subjectName || '',
          category: categoryName
        });
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [currentQuestion?.id, currentQuestionIndex, questions.length, subjectName, categoryName, updateQuizDetails]);

  // Separate effect for time and answers to avoid excessive updates
  useEffect(() => {
    if (currentQuestion) {
      const timeoutId = setTimeout(() => {
        updateQuizDetails({
          selectedAnswers,
          timeElapsed
        });
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedAnswers, timeElapsed, currentQuestion?.id, updateQuizDetails]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (!isCompleted) {
      intervalId = setInterval(() => {
        setTimeElapsed(prev => {
          const newTime = prev + 1;
          onTimeUpdate?.(newTime);
          return newTime;
        });
      }, 1000);
    }

    return () => clearInterval(intervalId);
  }, [isCompleted, onTimeUpdate]);

  const handleAnswerSelect = (optionId: string) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: optionId
    }));

    if (testMode === "practice") {
      setShowExplanation(true);
    } else if (testMode === "exam") {
      // In exam mode, automatically move to next question after selection
      setTimeout(() => {
        handleNext();
      }, 500);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setShowExplanation(false);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      setShowExplanation(testMode === "practice" && !!selectedAnswers[questions[currentQuestionIndex - 1]?.id]);
    }
  };

  const handleComplete = () => {
    const results = questions.map(q => ({
      questionId: q.id,
      selectedOptionId: selectedAnswers[q.id] || ""
    }));
    setIsCompleted(true);
    onSubmit(results);
  };

  if (!currentQuestion || questions.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="max-w-2xl mx-auto">
          <CardContent className="p-8 text-center">
            <p className="text-lg text-gray-600">No questions available for this test.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              {subjectName}
            </h1>
            <div className="flex items-center gap-4 mt-2">
              <Badge variant="secondary" className={testMode === "exam" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}>
                {testMode === "exam" ? "Exam Mode" : "Practice Mode"}
              </Badge>
              <div className="flex items-center gap-2 text-gray-600">
                <Clock className="h-4 w-4" />
                <span>{Math.floor(timeElapsed / 60)}:{(timeElapsed % 60).toString().padStart(2, '0')}</span>
              </div>
            </div>
          </div>
          
          <div className="text-right">
            <p className="text-sm text-gray-600">
              Question {currentQuestionIndex + 1} of {questions.length}
            </p>
            <Progress 
              value={(currentQuestionIndex + 1) / questions.length * 100} 
              className="w-32 mt-1"
            />
          </div>
        </div>

        {/* Question Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle
              className="text-xl prose prose-sm max-w-none [&_*]:text-inherit"
              dangerouslySetInnerHTML={{ __html: currentQuestion.text }}
            />
          </CardHeader>
          
          <CardContent>
            <div className="space-y-3">
              {currentQuestion.options.map((option) => {
                const isSelected = selectedAnswer === option.id;
                let optionStyle = "border-2 border-gray-200 hover:border-blue-300";
                
                if (showExplanation && testMode === "practice") {
                  if (option.is_correct) {
                    optionStyle = "border-2 border-green-500 bg-green-50";
                  } else if (isSelected && !option.is_correct) {
                    optionStyle = "border-2 border-red-500 bg-red-50";
                  }
                } else if (isSelected) {
                  optionStyle = "border-2 border-blue-500 bg-blue-50";
                }

                return (
                  <motion.button
                    key={option.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleAnswerSelect(option.id)}
                    disabled={showExplanation && testMode === "practice"}
                    className={`w-full p-4 text-left rounded-lg transition-all ${optionStyle} ${
                      showExplanation && testMode === "practice" ? 'cursor-default' : 'cursor-pointer'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span
                        className="prose prose-sm max-w-none [&_*]:text-inherit"
                        dangerouslySetInnerHTML={{ __html: option.text }}
                      />
                      {showExplanation && testMode === "practice" && (
                        <div>
                          {option.is_correct ? (
                            <CheckCircle className="h-5 w-5 text-green-600" />
                          ) : isSelected ? (
                            <XCircle className="h-5 w-5 text-red-600" />
                          ) : null}
                        </div>
                      )}
                    </div>
                  </motion.button>
                );
              })}
            </div>

            {/* Explanation for Practice Mode */}
            {showExplanation && testMode === "practice" && currentQuestion.explanation && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                transition={{ duration: 0.3 }}
                className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200"
              >
                <div className="flex items-start gap-3">
                  <BookOpen className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-blue-800 mb-2">Explanation</h4>
                    <p
                      className="text-blue-700 prose prose-sm max-w-none [&_*]:text-inherit"
                      dangerouslySetInnerHTML={{ __html: currentQuestion.explanation }}
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Previous
          </Button>

          <div className="flex gap-2">
            {testMode === "practice" && selectedAnswer && (
              <Button
                onClick={handleNext}
                className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white flex items-center gap-2"
              >
                {currentQuestionIndex === questions.length - 1 ? (
                  <>
                    <Flag className="h-4 w-4" />
                    Finish Test
                  </>
                ) : (
                  <>
                    Next
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            )}
            
            {testMode === "exam" && (
              <Button
                onClick={handleComplete}
                variant="outline"
                className="text-green-600 border-green-300 hover:bg-green-50 flex items-center gap-2"
              >
                <Flag className="h-4 w-4" />
                Submit Test
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default TestInterface;
